class ColorSwatch extends HTMLElement {
  constructor() {
    super();

    this.container = this.closest('.card-wrapper');

    this.addEventListener('mouseenter', this.colorSwatchHover.bind(this));
    this.addEventListener('touchstart', this.colorSwatchChange.bind(this), { passive: true });
    this.addEventListener('click', this.onClickHandler.bind(this));
    this.container.querySelector('.card__colors').addEventListener('mouseleave', this.colorSwatchResetChange.bind(this));

    this.observeCardForPrefetch();
  }

  colorSwatchHover() {
    this.container.classList.add('card-wrapper--swatch-hover');
    this.colorSwatchChange(false, true);
  }

  // Shared low-priority prefetch queue: two downloads at a time so warming
  // the cache never competes with the images the user is actually looking
  // at (or just tapped).
  static prefetchQueue = [];
  static prefetchActive = 0;

  static enqueuePrefetch(task) {
    ColorSwatch.prefetchQueue.push(task);
    ColorSwatch.drainPrefetchQueue();
  }

  static drainPrefetchQueue() {
    while (ColorSwatch.prefetchActive < 2 && ColorSwatch.prefetchQueue.length) {
      const task = ColorSwatch.prefetchQueue.shift();
      ColorSwatch.prefetchActive += 1;
      task().finally(() => {
        ColorSwatch.prefetchActive -= 1;
        ColorSwatch.drainPrefetchQueue();
      });
    }
  }

  // On touch devices the swap happens on the tap itself, so without a warm
  // cache the user watches the variant image download. Prefetch each
  // swatch's primary image once the card approaches the viewport, during
  // idle time.
  observeCardForPrefetch() {
    if (!this.container || this.container.dataset.swatchPrefetchObserved === 'true') return;
    this.container.dataset.swatchPrefetchObserved = 'true';

    const prefetch = () => {
      // timeout guarantees it fires even when the browser never reports idle
      // (e.g. a live socket keeping the page busy) — without it the whole
      // prefetch silently never runs
      const idle = window.requestIdleCallback
        ? (fn) => window.requestIdleCallback(fn, { timeout: 500 })
        : (fn) => setTimeout(fn, 200);
      idle(() => {
        // warm the current colour's HOVER image first (highest value — it's
        // the image the very next hover reveals; lazy-loading it on hover is
        // what flashes an empty grey box)
        const active = this.container.querySelector('.color-swatch.is-active, .color-swatch.swatch-selected') || this.container.querySelector('.color-swatch');
        if (active && active.dataset.hoverMediaUrl && active.dataset.hoverSwatchPrefetched !== 'true') {
          active.dataset.hoverSwatchPrefetched = 'true';
          const hover = cardMedia.swatchHover(active);
          ColorSwatch.enqueuePrefetch(() => this.preloadImage(hover.src, hover.srcset, true));
        }
        // Do not prefetch every colour and every gallery slide. On a PLP this
        // multiplied into dozens of image requests per visible
        // row, competing with the cards the shopper is actually reading. The
        // active hover image is the only asset needed for the first gesture;
        // selecting another colour still preloads that colour on demand.
      });
    };

    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        prefetch();
      });
    }, { rootMargin: '300px' });

    observer.observe(this.container);
  }

  onClickHandler(event) {
    // clicking selects the variant persistently on every device: hovering
    // other swatches still previews, but mouseleave returns to this one
    event.preventDefault();
    this.container.querySelectorAll('.color-swatch').forEach((swatch) => swatch.classList.remove('swatch-selected'));
    this.classList.add('swatch-selected');
    this.updateCardProductLinks(this);
    this.colorSwatchChange(true, true);
  }

  notifyCardMediaChanged() {
    this.container.dispatchEvent(new CustomEvent('card:media-changed', { bubbles: true }));
  }

  updateCardCoverClasses(swatch) {
    const coverOverride = this.container.dataset.cardCover;
    const primaryRatio = parseFloat(swatch.dataset.aspectRatio || '0');
    const hoverRatio = parseFloat(swatch.dataset.hoverAspectRatio || '0');
    const primaryIsTall = primaryRatio > 0 && (primaryRatio < 0.95 || primaryRatio > 1.05);

    if (coverOverride !== 'force' && coverOverride !== 'never' && primaryRatio > 0) {
      this.container.classList.toggle('card-wrapper--tall', primaryIsTall);
    }

    // While the pointer is on a swatch, the first image is deliberately
    // shown. The hover layout flag must therefore describe that first image,
    // not the hidden secondary image, or a square packshot inherits the
    // full-tile overlay and caption treatment from a portrait secondary.
    if (this.container.classList.contains('card-wrapper--swatch-hover') && primaryRatio > 0) {
      this.container.classList.toggle('card-wrapper--hover-tall', primaryIsTall);
      return;
    }

    if (hoverRatio > 0) {
      this.container.classList.toggle('card-wrapper--hover-tall', hoverRatio < 0.95 || hoverRatio > 1.05);
    }
    else {
      this.container.classList.remove('card-wrapper--hover-tall');
    }
  }

  updateCardQuickAdd(swatch) {
    const quickAddWrapper = this.container.querySelector('.card-information__quick-add');
    const quickAdd = this.container.querySelector('.card-quick-add');
    if (!quickAdd || !quickAddWrapper || !swatch.dataset.variantId) return;

    const unavailable = swatch.hasAttribute('data-cross-out');
    quickAdd.dataset.variantId = unavailable ? '' : swatch.dataset.variantId;
    quickAddWrapper.hidden = unavailable;
    quickAddWrapper.classList.toggle('is-hidden', unavailable);
    quickAdd.classList.toggle('is-disabled', unavailable);
    quickAdd.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
  }

  updateCardProductLinks(swatch) {
    const swatchLink = swatch.querySelector('a[href]');
    if (!swatchLink) return;

    const swatchUrl = new URL(swatchLink.getAttribute('href'), window.location.origin);
    const variantId = swatch.dataset.variantId || swatchUrl.searchParams.get('variant');
    if (!variantId) return;

    this.container.querySelectorAll('[data-card-product-link]').forEach((link) => {
      const productUrl = new URL(link.getAttribute('href'), window.location.origin);
      productUrl.searchParams.set('variant', variantId);
      link.setAttribute('href', `${productUrl.pathname}${productUrl.search}${productUrl.hash}`);
    });
  }

  formatPrice(cents) {
    return '<price-money><bdi>' + theme.Currency.formatMoneyWithoutTrailingZeros(cents, theme.shopSettings.moneyFormat) + '</bdi></price-money>';
  }

  updateCardPrice(swatch) {
    const priceWrapper = this.container.querySelector('.price');
    if (!priceWrapper || !swatch || !swatch.dataset.price) return;

    const price = parseInt(swatch.dataset.price || '0', 10);
    const compareAtPrice = parseInt(swatch.dataset.compareAtPrice || swatch.dataset.price || '0', 10);
    const isAvailable = swatch.dataset.available !== 'false';
    const isOnSale = compareAtPrice > price;
    const regularPrice = this.container.querySelector('.price__regular .price-item--regular');
    const comparePrice = this.container.querySelector('.price__sale .price-item--regular');
    const salePrice = this.container.querySelector('.price__sale .price-item--sale');

    priceWrapper.classList.toggle('price--sold-out', !isAvailable);
    priceWrapper.classList.toggle('price--on-sale', isOnSale);
    priceWrapper.classList.remove('price--no-compare');

    if (regularPrice) {
      regularPrice.innerHTML = this.formatPrice(price);
    }
    if (comparePrice) {
      comparePrice.innerHTML = this.formatPrice(isOnSale ? compareAtPrice : price);
    }
    if (salePrice) {
      salePrice.innerHTML = this.formatPrice(price);
    }
  }

  // The sizes value MUST match the rendered card image's own sizes
  // attribute — a mismatch makes the browser pick a different srcset
  // candidate than the swap will request, so the prefetch caches the wrong
  // URL and the tap still downloads cold.
  cardImageSizes() {
    const rendered = this.container.querySelector('.media > img');
    return (rendered && rendered.getAttribute('sizes')) || '(min-width: 990px) 25vw, (min-width: 750px) 34vw, 60vw';
  }

  preloadImage(src, srcset, lowPriority = false) {
    return new Promise((resolve) => {
      if (!src) {
        resolve();
        return;
      }

      const image = new Image();
      const finish = () => {
        if (image.decode) {
          image.decode().catch(() => {}).finally(resolve);
        }
        else {
          resolve();
        }
      };

      image.onload = finish;
      image.onerror = resolve;
      if (lowPriority) image.fetchPriority = 'low';
      image.sizes = this.cardImageSizes();
      if (srcset) image.srcset = srcset;
      image.src = src;
    });
  }

  preloadSwatchMedia(swatch) {
    const sources = [cardMedia.swatchPrimary(swatch), cardMedia.swatchHover(swatch)];

    const extraUrls = (swatch.dataset.galleryExtraUrls || '').split('||').filter(Boolean);
    const extraWidths = (swatch.dataset.galleryExtraWidths || '').split('||');
    extraUrls.forEach((url, index) => {
      sources.push(cardMedia.swatchImage(url, extraWidths[index]));
    });

    const seen = new Set();
    return Promise.all(sources.filter(({ src }) => {
      if (!src || seen.has(src)) return false;
      seen.add(src);
      return true;
    }).map(({ src, srcset }) => this.preloadImage(src, srcset)));
  }

  shouldDeferMediaSwap() {
    return window.matchMedia('(hover: hover) and (min-width: 990px)').matches && this.container.matches(':hover');
  }

  applySwatchChange(swatch, forceUpdate) {
    const image = this.container.querySelector('.card--image-animate.media img');
    const secondaryImage = this.container.querySelector('.card--image-animate.media img + img');

    if (image !== null || forceUpdate) {
      if (image !== null) {
        // the swapped-in image is what the user is looking at right now
        const primary = cardMedia.swatchPrimary(swatch);
        image.loading = 'eager';
        image.setAttribute('fetchpriority', 'high');
        image.src = primary.src;
        image.srcset = primary.srcset;
      }

      if (secondaryImage !== null) {
        const hover = cardMedia.swatchHover(swatch);
        secondaryImage.src = hover.src;
        secondaryImage.srcset = hover.srcset;
      }

      this.updateCardCoverClasses(swatch);
      this.updateCardQuickAdd(swatch);
      this.updateCardPrice(swatch);

      const swatches = this.container.querySelectorAll('.color-swatch');
      swatches.forEach((swatch) => {
        swatch.classList.remove('is-active');
      });
  
      swatch.classList.add('is-active');

      // Revert div to a href for wrapper (remove the inline display:none of
      // video-featured cards; keeps the stylesheet display — flex in gallery mode)
      this.container.querySelector('.card--image-animate.media').style.removeProperty('display');
      if(this.container.querySelector('.card-media-video')) {
        this.container.querySelector('.card-media-video').style.display = "none";
      }

      this.updateCardGallery(swatch);
      this.notifyCardMediaChanged();
      this.container._cardMediaLanded = { swatch, swapId: this.container._cardMediaSwapId };
    }
  }

  // A tap fires touchstart, then the browser's emulated mouseenter, then
  // click; a desktop click follows the hover that already previewed the
  // colour. When this swatch's swap is the card's latest and has landed, the
  // follow-up events skip redoing it (image sources, price, gallery rebuild,
  // the layout forced by scrollTo). The hover layout flag still follows the
  // swatch-hover class that mouseenter adds, and listeners still re-sync.
  // touchstart never reuses: a re-tap after a gallery swipe must rewind it.
  reuseLandedSwap() {
    const landed = this.container._cardMediaLanded;
    if (!landed || landed.swatch !== this || landed.swapId !== this.container._cardMediaSwapId) return false;

    this.updateCardCoverClasses(this);
    this.notifyCardMediaChanged();
    return true;
  }

  colorSwatchChange(forceUpdate, reuseLandedSwap = false) {
    if (reuseLandedSwap && this.reuseLandedSwap()) return;

    const swapId = (this.container._cardMediaSwapId || 0) + 1;
    this.container._cardMediaSwapId = swapId;

    if (!this.shouldDeferMediaSwap()) {
      this.container.classList.remove('card-wrapper--media-swapping');
      this.applySwatchChange(this, forceUpdate);
      return;
    }

    this.container.classList.add('card-wrapper--media-swapping');

    this.preloadSwatchMedia(this).then(() => {
      if (this.container._cardMediaSwapId !== swapId) return;

      this.applySwatchChange(this, forceUpdate);
      requestAnimationFrame(() => {
        if (this.container._cardMediaSwapId === swapId) {
          this.container.classList.remove('card-wrapper--media-swapping');
        }
      });
    });
  }

  updateCardGallery(swatch) {
    const media = this.container.querySelector('.card--image-animate.media');
    if (media === null || !media.classList.contains('card__media-gallery')) return;

    const items = cardMedia.swatchGalleryItems(swatch);
    if (items.length === 0) return;

    const existingImages = Array.from(media.querySelectorAll(':scope > img'));
    const templateImage = existingImages[0];
    if (!templateImage) return;
    const secondaryMobileOnly = media.dataset.secondaryMobileOnly === 'true';

    items.forEach((item, index) => {
      const image = existingImages[index] || templateImage.cloneNode(false);
      const fitClass = item.ratio && (item.ratio < 0.95 || item.ratio > 1.05)
        ? 'card__media-slide--cover'
        : 'card__media-slide--contain';
      const slideClasses = `card__media-slide ${fitClass}`;

      image.className = index === 0
        ? `motion-reduce ${slideClasses}`
        : index === 1
          ? `motion-reduce card__media-secondary${secondaryMobileOnly ? ' card__media-secondary--mobile-only' : ''} ${slideClasses}`
          : `motion-reduce card__media-extra ${slideClasses}`;

      // After a swatch tap, every slide for the selected colour should be
      // ready before the user swipes horizontally.
      image.loading = 'eager';
      if (index === 0) {
        image.setAttribute('fetchpriority', 'high');
      }
      else {
        image.setAttribute('fetchpriority', 'low');
      }

      if (item.srcset) {
        image.srcset = item.srcset;
      }
      else {
        image.removeAttribute('srcset');
      }

      image.src = item.src;
      image.alt = item.alt;

      if (!image.parentNode) {
        media.appendChild(image);
      }
    });

    existingImages.slice(items.length).forEach((image) => image.remove());
    media.classList.remove('card__media-gallery--filtered');
    media.scrollTo({ left: 0, behavior: 'auto' });
  }

  colorSwatchResetChange() {
    this.container.classList.remove('card-wrapper--swatch-hover');
    // the pointer left the swatch row, so the next swatch event starts afresh
    this.container._cardMediaLanded = null;

    // touch devices fire a synthetic mouseleave after tapping elsewhere, which
    // would revert the tapped swatch back to the first one — desktop-only
    if (window.matchMedia('(max-width: 749px)').matches) return;

    this.container._cardMediaSwapId = (this.container._cardMediaSwapId || 0) + 1;
    this.container.classList.remove('card-wrapper--media-swapping');

    // hover previews restore to the clicked (persistent) swatch when there is
    // one, otherwise to the first
    const restoreSwatch = this.container.querySelector('.color-swatch.swatch-selected') || this.container.querySelectorAll('.color-swatch')[0];

    const image = this.container.querySelector('.card--image-animate.media img');
    const secondaryImage = this.container.querySelector('.card--image-animate.media img + img');

    if (image !== null) {
      const primary = cardMedia.swatchPrimary(restoreSwatch);
      image.src = primary.src;
      image.srcset = primary.srcset;
    }

    if (secondaryImage !== null) {
      const hover = cardMedia.swatchHover(restoreSwatch);
      secondaryImage.src = hover.src;
      secondaryImage.srcset = hover.srcset;
    }

    this.updateCardCoverClasses(restoreSwatch);
    this.updateCardQuickAdd(restoreSwatch);
    this.updateCardPrice(restoreSwatch);

    const swatches = this.container.querySelectorAll('.color-swatch');
    swatches.forEach((swatch) => {
      if(restoreSwatch == swatch) {
        swatch.classList.add('is-active');
      } else {
        swatch.classList.remove('is-active');
      }
    });

    this.updateCardGallery(restoreSwatch);
    this.notifyCardMediaChanged();
  }
}
customElements.define('color-swatch', ColorSwatch);

class ColorSwatchRadios extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      soldOut: 'price--sold-out',
      onSale: 'price--on-sale',
      noCompare: 'price--no-compare',
      hidden: 'hidden',
      loading: 'loading'
    };
    this.selectors = {
      cardWrapper: '.card-wrapper',
      priceWrapper: '.price',
      price: '.price__regular .price-item--regular',
      comparePrice: '.price__sale .price-item--regular',
      salePrice: '.price__sale .price-item--sale',
      unitWrapper: '.unit-price',
      unitPrice: '[data-unit-price]',
      unitPriceBaseUnit: '[data-unit-base]'
    };

    this.miniCart = document.querySelector('mini-cart');

    this.init();
    this.addEventListener('change', this.onVariantChange);

    const buttons = this.querySelectorAll('.swatch:not(.disabled)');
    buttons.forEach((button) => button.addEventListener('mouseenter', this.onEnterHandler.bind(this)));
    buttons.forEach((button) => button.addEventListener('mouseleave', this.onLeaveHandler.bind(this)));

    const inputs = this.querySelectorAll('.swatch:not(.disabled) input');
    inputs.forEach((input) => input.addEventListener('click', this.onClickHandler.bind(this)));
  }

  cacheElements() {
    this.container = this.closest(this.selectors.cardWrapper);
    this.cache = {
      priceWrapper: this.container.querySelector(this.selectors.priceWrapper),
      price: this.container.querySelector(this.selectors.price),
      comparePrice: this.container.querySelector(this.selectors.comparePrice),
      salePrice: this.container.querySelector(this.selectors.salePrice),
      priceHTML: this.container.querySelector(this.selectors.priceWrapper).cloneNode(true).innerHTML,
      priceClass: this.container.querySelector(this.selectors.priceWrapper).cloneNode(true).getAttribute('class'),
    };
  }

  onClickHandler(event) {
    const input = event.currentTarget;
    const button = input.parentNode;
    const variantId = this.currentVariant.id;

    if (variantId) {
      if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
        Shopify.postLink(theme.routes.cart_add_url, {
          parameters: {
            id: variantId,
            quantity: 1
          },
        });
        return;
      }

      input.setAttribute('disabled', '');
      button.classList.add(this.classes.loading);
      const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.id) : [];

      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sections,
        sections_url: window.location.pathname
      });

      fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then((parsedState) => {
          this.miniCart && this.miniCart.renderContents(parsedState);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          input.removeAttribute('disabled');
          button.classList.remove(this.classes.loading);
        });
    }
  }

  onEnterHandler(event) {
    const target = event.currentTarget;
    const input = target.querySelector('input');
    input.checked = true;
    this.dispatchEvent(new Event('change'));

    setTimeout(() => {
      this.cacheElements();
      this.updatePrice();
      this.updateUnitPrice();
    });
  }

  onLeaveHandler() {
    this.cache.priceWrapper.innerHTML = this.cache.priceHTML;
    this.cache.priceWrapper.setAttribute('class', this.cache.priceClass);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
  }

  init() {
    this.updateOptions();
    this.updateMasterId();
    this.setAvailability();
  }

  updatePrice() {
    const variant = this.currentVariant;
  
    if (variant) {
      // Remove classes
      this.cache.priceWrapper.classList.remove(this.classes.soldOut);
      this.cache.priceWrapper.classList.remove(this.classes.onSale);
      this.cache.priceWrapper.classList.remove(this.classes.noCompare);

      if (variant.available == false) {
        this.cache.priceWrapper.classList.add(this.classes.soldOut);
      }
      else if (variant.compare_at_price > variant.price) {
        this.cache.priceWrapper.classList.add(this.classes.onSale);
      }

      // Regular price
      this.cache.price.innerHTML = '<price-money><bdi>' + theme.Currency.formatMoneyWithoutTrailingZeros(variant.price, theme.shopSettings.moneyFormat) + '</bdi></price-money>';

      // Sale price, if necessary
      if (variant.compare_at_price > variant.price) {
        this.cache.comparePrice.innerHTML = '<price-money><bdi>' + theme.Currency.formatMoneyWithoutTrailingZeros(variant.compare_at_price, theme.shopSettings.moneyFormat) + '</bdi></price-money>';
        this.cache.salePrice.innerHTML = '<price-money><bdi>' + theme.Currency.formatMoneyWithoutTrailingZeros(variant.price, theme.shopSettings.moneyFormat) + '</bdi></price-money>';
      }
      else {
        
      }
    }
  }

  updateUnitPrice() {
    const variant = this.currentVariant;
  
    if (this.container.querySelector(this.selectors.unitWrapper)) {
      if (variant && variant.unit_price) {
        this.container.querySelector(this.selectors.unitPrice).innerHTML = '<price-money><bdi>' + theme.Currency.formatMoneyWithoutTrailingZeros(variant.unit_price, theme.shopSettings.moneyFormat) + '</bdi></price-money>';
        this.container.querySelector(this.selectors.unitPriceBaseUnit).innerHTML = theme.Currency.getBaseUnit(variant);
        this.container.querySelector(this.selectors.unitWrapper).classList.remove(this.classes.hidden);
      }
      else {
        this.container.querySelector(this.selectors.unitWrapper).classList.add(this.classes.hidden);
      }
    }
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData()?.find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  setAvailability() {
    if (!this.currentVariant) return;

    this.querySelectorAll('.variant-input-wrapper').forEach(group => {
      this.disableVariantGroup(group);
    });

    const currentlySelectedValues = this.currentVariant.options.map((value,index) => {return {value, index: `option${index+1}`}})
    const initialOptions = this.createAvailableOptionsTree(this.variantData, currentlySelectedValues, this.currentVariant);

    for (var [option, values] of Object.entries(initialOptions)) {
      this.manageOptionState(option, values);
    }
  }

  enableVariantOption(group, obj) {
    const value = obj.value.replace(/([ #;&,.+*~\':"!^$[\]()=>|\/@])/g,'\\$1');
    const input = group.querySelector('input[data-option-value="'+ value +'"]');

    if (!input) return;

    // Variant exists - enable & show variant
    input.removeAttribute('disabled');
    input.parentNode.classList.remove('disabled');

    // Variant sold out - cross out option (remains selectable)
    if (obj.soldOut) {
      input.setAttribute('disabled', '');
      input.parentNode.classList.add('disabled');
    }
  }

  disableVariantGroup(group) {
    group.querySelectorAll('input').forEach((input) => {
      input.setAttribute('disabled', '');
      input.parentNode.classList.add('disabled');
    });
  }

  manageOptionState(option, values) {
    const group = this.querySelector('.variant-input-wrapper[data-option-index="'+ option +'"]');

    // Loop through each option value
    values.forEach(obj => {
      this.enableVariantOption(group, obj);
    });
  }

  createAvailableOptionsTree(variants, currentlySelectedValues) {
    // Reduce variant array into option availability tree
    return variants.reduce((options, variant) => {

      // Check each option group (e.g. option1, option2, option3) of the variant
      Object.keys(options).forEach(index => {

        if (variant[index] === null) return;

        let entry = options[index].find(option => option.value === variant[index]);

        if (typeof entry === 'undefined') {
          // If option has yet to be added to the options tree, add it
          entry = {value: variant[index], soldOut: true}
          options[index].push(entry);
        }

        const currentOption1 = currentlySelectedValues.find(({value, index}) => index === 'option1')
        const currentOption2 = currentlySelectedValues.find(({value, index}) => index === 'option2')

        switch (index) {
          case 'option1':
            // Option1 inputs should always remain enabled based on all available variants
            entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
            break;
          case 'option2':
            // Option2 inputs should remain enabled based on available variants that match first option group
            if (currentOption1 && variant['option1'] === currentOption1.value) {
              entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
            }
          case 'option3':
            // Option 3 inputs should remain enabled based on available variants that match first and second option group
            if (
              currentOption1 && variant['option1'] === currentOption1.value
              && currentOption2 && variant['option2'] === currentOption2.value
            ) {
              entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut;
            }
        }
      })

      return options;
    }, { option1: [], option2: [], option3: []})
  }
}
customElements.define('color-swatch-radios', ColorSwatchRadios);
